class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    this.mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.mainForm) return;

    this.mainQtyInput = this.mainForm.querySelector('input[name="quantity"]');
    this.mainAtcBtn = document.querySelector('[data-js-product-add-to-cart]');

    this.stickySelect = this.querySelector('.sticky-buy-bar__variant-select');
    this.stickyQtyInput = this.querySelector('.sticky-buy-bar__qty-input');

    this._controller = new AbortController();

    this.setupVisibility();
    this.setupVariantSync();
    this.setupQuantitySync();
    this.setupAtcButton();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this._controller) this._controller.abort();
  }

  setupVisibility() {
    if (!this.mainAtcBtn) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-hidden', entry.isIntersecting);
    });
    this.observer.observe(this.mainAtcBtn);
  }

  setupVariantSync() {
    const { signal } = this._controller;
    const productVariants = document.querySelector('product-variants');

    // Main → sticky: sync select value and disabled state on every variant change
    if (productVariants) {
      productVariants.addEventListener('VARIANT_CHANGE', () => {
        const variant = productVariants.currentVariant;
        if (!variant) return;
        if (this.stickySelect) this.stickySelect.value = String(variant.id);
        this._setAvailability(variant.available);
      }, { signal });
    }

    // Sticky → main: only relevant when a variant select exists
    if (productVariants && this.stickySelect) {
      let variantMap = [];
      try { variantMap = JSON.parse(this.dataset.variantMap || '[]'); } catch (_) {}

      this.stickySelect.addEventListener('change', () => {
        const variantId = parseInt(this.stickySelect.value, 10);
        const variant = variantMap.find(v => v.id === variantId);
        if (!variant) return;

        // Update radio-style pickers (one container per option)
        const radioContainers = productVariants.querySelectorAll('[data-js-product-variant-container="radio"]');
        radioContainers.forEach((container, i) => {
          const optVal = variant.options[i];
          if (optVal === undefined) return;
          const radios = container.querySelectorAll('input[type="radio"]');
          radios.forEach(r => { r.checked = (r.value === optVal); });
        });

        // Update select-style pickers (one <select> per option)
        const selectContainers = productVariants.querySelectorAll('[data-js-product-variant-container="select"]');
        selectContainers.forEach((sel, i) => {
          const optVal = variant.options[i];
          if (optVal !== undefined) sel.value = optVal;
        });

        // Fire one change on product-variants so onVariantChange reads the updated inputs
        productVariants.dispatchEvent(new Event('change', { bubbles: false }));
      }, { signal });
    }
  }

  setupQuantitySync() {
    if (!this.stickyQtyInput) return;
    const { signal } = this._controller;

    const decreaseBtn = this.querySelector('[data-action="decrease"]');
    const increaseBtn = this.querySelector('[data-action="increase"]');
    if (!decreaseBtn || !increaseBtn) return;

    decreaseBtn.addEventListener('click', () => {
      const val = parseInt(this.stickyQtyInput.value, 10) || 1;
      if (val > 1) {
        this.stickyQtyInput.value = val - 1;
        this.syncQtyToMain();
      }
    }, { signal });

    increaseBtn.addEventListener('click', () => {
      const val = parseInt(this.stickyQtyInput.value, 10) || 1;
      this.stickyQtyInput.value = val + 1;
      this.syncQtyToMain();
    }, { signal });

    this.stickyQtyInput.addEventListener('change', () => this.syncQtyToMain(), { signal });

    // Main → sticky: listen on the main qty input and its +/- buttons
    if (this.mainQtyInput) {
      this.mainQtyInput.addEventListener('change', () => {
        this.stickyQtyInput.value = this.mainQtyInput.value;
      }, { signal });

      const mainQtyContainer = this.mainQtyInput.closest('[data-js-product-quantity]');
      if (mainQtyContainer) {
        mainQtyContainer.querySelector('.qty-minus')?.addEventListener('click', () => {
          setTimeout(() => { this.stickyQtyInput.value = this.mainQtyInput.value; }, 0);
        }, { signal });
        mainQtyContainer.querySelector('.qty-plus')?.addEventListener('click', () => {
          setTimeout(() => { this.stickyQtyInput.value = this.mainQtyInput.value; }, 0);
        }, { signal });
      }
    }
  }

  syncQtyToMain() {
    if (this.mainQtyInput) {
      this.mainQtyInput.value = this.stickyQtyInput.value;
    }
  }

  _setAvailability(available) {
    const stickyAtc = this.querySelector('.sticky-buy-bar__atc');
    if (stickyAtc) {
      stickyAtc.disabled = !available;
      stickyAtc.classList.toggle('disabled', !available);
    }

    const decreaseBtn = this.querySelector('[data-action="decrease"]');
    const increaseBtn = this.querySelector('[data-action="increase"]');
    [decreaseBtn, increaseBtn].forEach(btn => {
      if (btn) btn.disabled = !available;
    });

    if (this.stickyQtyInput) {
      if (available) {
        this.stickyQtyInput.type = 'number';
        this.stickyQtyInput.value = this.mainQtyInput ? this.mainQtyInput.value : '1';
        this.stickyQtyInput.disabled = false;
      } else {
        this.stickyQtyInput.type = 'text';
        this.stickyQtyInput.value = '-';
        this.stickyQtyInput.disabled = true;
      }
    }
  }

  setupAtcButton() {
    if (!this.mainAtcBtn) return;
    this.querySelector('.sticky-buy-bar__atc').addEventListener('click', () => {
      this.mainAtcBtn.click();
    }, { signal: this._controller.signal });
  }
}

customElements.define('sticky-buy-bar', StickyBuyBar);
